import { useEffect, useRef, useState } from "react";
import { AlertCircle, Check, Copy, Loader, Save } from "lucide-react";
import { Sheet } from "./ui/Sheet";
import { Button } from "./ui/Button";
import { useDesign } from "../lib/store";
import { useLibrary } from "../lib/library";
import { callCreateSharedRoom, collabLimitFromError } from "../lib/sharedInvites";
import { requestUpsell } from "../lib/upsellRequests";
import { cn } from "../lib/cn";

interface Props {
  open: boolean;
  onClose: () => void;
  defaultName: string;
  sourceRoomName: string;
}

/**
 * Confirmation sheet for forking a shared room into the user's library.
 * The copy is always a new private SavedRoom; sharing is opt-in and
 * creates a *separate* shared room the user owns. The current
 * multiplayer session is left untouched.
 */
export function SaveCopySheet({
  open,
  onClose,
  defaultName,
  sourceRoomName,
}: Props) {
  return (
    <Sheet open={open} onClose={onClose} title="Save a copy" side="right">
      <Body
        open={open}
        onClose={onClose}
        defaultName={defaultName}
        sourceRoomName={sourceRoomName}
      />
    </Sheet>
  );
}

function Body({
  open,
  onClose,
  defaultName,
  sourceRoomName,
}: Props) {
  const design = useDesign((s) => s.design);
  const saveDesign = useLibrary((s) => s.saveDesign);

  const [name, setName] = useState(defaultName);
  const [shareOn, setShareOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);
  const closeTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(defaultName);
    setShareOn(false);
    setBusy(false);
    setSaved(false);
    setError(null);
    setSavedId(null);
    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, [open, defaultName]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current != null) {
        window.clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  const trimmed = name.trim();
  const alreadySaved = savedId !== null;
  const canSubmit = trimmed.length > 0 && !busy && !saved;

  async function handleSave() {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const id = savedId ?? saveDesign(design, trimmed);
      setSavedId(id);
      if (shareOn) {
        try {
          await callCreateSharedRoom({
            roomId: id,
            name: trimmed,
            design,
          });
        } catch (err) {
          if (collabLimitFromError(err)) {
            onClose();
            requestUpsell("collab_unlimited", "limit");
            return;
          }
          setError(
            `Copy saved, but sharing couldn't be enabled: ${asMessage(err)}`
          );
          return;
        }
      }
      setSaved(true);
      closeTimerRef.current = window.setTimeout(() => {
        closeTimerRef.current = null;
        onClose();
      }, 1200);
    } finally {
      setBusy(false);
    }
  }

  if (saved) {
    return (
      <div
        className="flex flex-col items-center gap-3 pt-6 pb-4 text-center"
        data-testid="save-copy-saved"
      >
        <div className="size-11 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
          <Check className="size-5" />
        </div>
        <div className="text-sm font-semibold text-ink-900">
          Saved to your library
        </div>
        <p className="text-caption text-ink-500 max-w-[22rem]">
          {shareOn
            ? "It's a separate room you own. Invite people from Rooms → Share."
            : "It's a private copy — this shared room is unchanged."}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 pt-1" data-testid="save-copy-sheet">
      <div className="rounded-2xl bg-ink-50 ring-1 ring-ink-100 px-4 py-4 flex items-start gap-3">
        <div className="size-9 shrink-0 rounded-xl bg-white text-ink-700 ring-1 ring-ink-200 flex items-center justify-center">
          <Copy className="size-4" />
        </div>
        <p className="text-caption text-ink-500 leading-relaxed">
          This saves a separate copy to your library. Changes in the copy
          won&apos;t sync with{" "}
          <span className="font-semibold text-ink-700">{sourceRoomName}</span>,
          and the copy isn&apos;t shared with anyone unless you turn sharing
          on below.
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <label
          htmlFor="save-copy-name"
          className="text-caption font-medium text-ink-700"
        >
          Copy name
        </label>
        <input
          id="save-copy-name"
          data-testid="save-copy-name"
          type="text"
          autoFocus
          value={name}
          disabled={busy || alreadySaved}
          placeholder="Untitled room"
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void handleSave();
          }}
          className="h-10 px-3 rounded-xl bg-white ring-1 ring-ink-200 text-sm text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500/40 disabled:opacity-50"
        />
      </div>

      <div className="flex items-center justify-between gap-3 p-3 rounded-xl bg-ink-50 ring-1 ring-ink-100">
        <div className="min-w-0">
          <div className="text-body-sm font-semibold text-ink-900">
            Share this copy
          </div>
          <div className="text-caption text-ink-500 leading-relaxed">
            Creates a shared room you own. You can invite people from Rooms
            → Share afterwards.
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={shareOn}
          aria-label="Share this copy"
          data-testid="save-copy-share-toggle"
          disabled={busy}
          onClick={() => setShareOn((v) => !v)}
          className={cn(
            "relative h-6 w-11 shrink-0 rounded-full transition-colors disabled:opacity-50",
            shareOn ? "bg-ink-900" : "bg-ink-300"
          )}
        >
          <span
            className={cn(
              "absolute top-0.5 size-5 rounded-full bg-white shadow transition-all",
              shareOn ? "left-[22px]" : "left-0.5"
            )}
          />
        </button>
      </div>

      {error && (
        <div
          data-testid="save-copy-error"
          className="rounded-xl bg-rose-50 ring-1 ring-rose-200 px-3 py-2.5 text-caption text-rose-700 flex items-start gap-2"
        >
          <AlertCircle className="size-4 mt-0.5 shrink-0" />
          <div>{error}</div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2 pt-1">
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={busy}
          className="flex-1"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => void handleSave()}
          disabled={!canSubmit}
          data-testid="save-copy-submit"
          className="flex-1"
        >
          {busy ? (
            <Loader className="size-4 animate-spin" />
          ) : (
            <>
              <Save className="size-4" /> Save copy
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

function asMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return typeof err === "string" ? err : "Something went wrong.";
}
